/**
 * NotificationsContext - 알림 상태 관리
 *
 * @CODE:API-201 | SPEC: olulo-mx-admin/.claude/specs/SPEC-201-notification-api.md
 *
 * 앱 전체의 알림 상태를 제공합니다.
 * - 로그인 시: 백엔드 API에서 알림 조회
 * - 비로그인 시: 빈 상태 표시
 * - 실시간 알림: Laravel Echo 연동 (Phase 2)
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import type { Notification } from '@/components/notification-dropdown/NotificationDropdown';
import { useApp } from './AppContext';
import { useAuth } from './AuthContext';
import { generateUUID } from '@/lib/app-utils';
import {
  notificationApi,
  transformApiNotifications,
  transformBroadcastNotification,
} from '@/lib/api/notifications';
import type { BroadcastNotificationPayload } from '@/lib/api/notifications';
import { initializeEcho, disconnectEcho, getEcho } from '@/lib/echo';
import { refreshPushToken, setEchoNotificationsActive } from '@/lib/push';
import { logger } from '@/utils/logger';
import { safeErrorMetadata } from '@/utils/safeErrorMetadata';
import { apiClient } from '@/lib/api/client';
import { toast } from 'sonner';

interface NotificationsContextValue {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  deleteAllNotifications: () => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  refreshNotifications: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

interface NotificationsProviderProps {
  children: ReactNode;
}

// 폴링 간격 (30초)
const POLLING_INTERVAL = 30000;

export function NotificationsProvider({ children }: NotificationsProviderProps) {
  // language는 향후 다국어 알림 지원 시 사용 예정
  const { language: _language } = useApp();
  const { isLoggedIn, customerId } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const echoInitializedRef = useRef(false);
  const channelSubscribedRef = useRef<string | null>(null); // 현재 구독 중인 채널
  const notificationsRef = useRef<Notification[]>([]); // 실시간 콜백 중복 체크용 ref 미러

  const updateNotifications = useCallback((next: Notification[] | ((previous: Notification[]) => Notification[])) => {
    const value = typeof next === 'function' ? next(notificationsRef.current) : next;
    notificationsRef.current = value;
    setNotifications(value);
  }, []);

  // Each authenticated identity owns its reads; old responses cannot repopulate
  // notification state after logout/account changes, even if a transport ignores abort.
  const readScope = useMemo(() => ({
    identity: { isLoggedIn, customerId },
    controller: new AbortController(),
    revision: 0,
    pendingMutations: 0,
    needsReconcile: false,
    list: null as Promise<void> | null,
    unread: null as Promise<void> | null,
  }), [isLoggedIn, customerId]);
  const readScopeRef = useRef(readScope);
  useLayoutEffect(() => {
    readScopeRef.current = readScope;
    updateNotifications([]);
    setUnreadCount(0);
    setIsLoading(false);
    return () => readScope.controller.abort();
  }, [readScope, updateNotifications]);
  const isCurrentRead = useCallback(() =>
    readScopeRef.current === readScope && !readScope.controller.signal.aborted,
  [readScope]);

  const fetchNotifications = useCallback(async () => {
    if (!isLoggedIn || !isCurrentRead()) return;
    if (readScope.pendingMutations > 0) {
      readScope.needsReconcile = true;
      return;
    }
    if (readScope.list) return readScope.list;
    const controller = readScope.controller;
    const revision = readScope.revision;
    const authEpoch = apiClient.getAuthEpoch();
    const ownsRead = () => isCurrentRead() && readScope.controller === controller && readScope.revision === revision;
    const active = () => ownsRead() && readScope.pendingMutations === 0 && apiClient.getAuthEpoch() === authEpoch;
    readScope.list = (async () => {
      setIsLoading(true);
      try {
        const response = await notificationApi.getNotifications({ per_page: 50 }, controller.signal);
        if (!active()) return;
        updateNotifications(transformApiNotifications(response.data));
        setUnreadCount(response.meta.unread_count);
      } catch (error) {
        if (active()) {
          logger.error('[Notifications] Failed to fetch:', {
            diagnostic_marker: 'menu.notifications.unread.v1',
            operation: 'list-read',
            error: safeErrorMetadata(error) ?? { type: 'Unknown' },
          });
        }
      } finally {
        // Authentication may change without committing a new React identity.
        // Release this read's UI ownership even when its response is discarded.
        if (ownsRead()) setIsLoading(false);
        if (readScope.controller === controller && readScope.revision === revision) readScope.list = null;
      }
    })();
    return readScope.list;
  }, [isLoggedIn, isCurrentRead, readScope, updateNotifications]);

  const fetchUnreadCount = useCallback(async () => {
    if (!isLoggedIn || !isCurrentRead() || readScope.pendingMutations > 0) return;
    if (readScope.unread) return readScope.unread;
    const controller = readScope.controller;
    const revision = readScope.revision;
    const authEpoch = apiClient.getAuthEpoch();
    const ownsRead = () => isCurrentRead() && readScope.controller === controller && readScope.revision === revision;
    const active = () => ownsRead() && readScope.pendingMutations === 0 && apiClient.getAuthEpoch() === authEpoch;
    readScope.unread = (async () => {
      try {
        const response = await notificationApi.getUnreadCount(controller.signal);
        if (active()) setUnreadCount(response.data.count);
      } catch (error) {
        if (active()) {
          logger.error('[Notifications] Failed to fetch unread count:', {
            diagnostic_marker: 'menu.notifications.unread.v1',
            operation: 'unread-read',
            error: safeErrorMetadata(error) ?? { type: 'Unknown' },
          });
        }
      } finally {
        if (readScope.controller === controller && readScope.revision === revision) readScope.unread = null;
      }
    })();
    return readScope.unread;
  }, [isLoggedIn, isCurrentRead, readScope]);

  // 알림 새로고침
  const refreshNotifications = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    // StrictMode replays effect setup after cleanup using the same memoized scope.
    if (readScope.controller.signal.aborted) {
      readScope.controller = new AbortController();
      readScope.list = null;
      readScope.unread = null;
    }
    if (isLoggedIn) void fetchNotifications();
    return () => readScope.controller.abort();
  }, [isLoggedIn, readScope, fetchNotifications]);

  // Background reads pause while offline/hidden and never overlap. Resuming only
  // reads current state; notification mutations are not automatically replayed.
  useEffect(() => {
    if (!isLoggedIn) return;
    const poll = () => {
      if (document.visibilityState === 'hidden' || navigator.onLine === false) return;
      void fetchUnreadCount();
    };
    pollingRef.current = setInterval(poll, POLLING_INTERVAL);
    document.addEventListener('visibilitychange', poll);
    window.addEventListener('online', poll);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = null;
      document.removeEventListener('visibilitychange', poll);
      window.removeEventListener('online', poll);
    };
  }, [isLoggedIn, fetchUnreadCount]);

  // Laravel Echo 실시간 알림 (@CODE:API-201)
  useEffect(() => {
    // 로그아웃 시 Echo 정리
    if (!isLoggedIn) {
      if (channelSubscribedRef.current) {
        const echo = getEcho();
        if (echo) {
          echo.leave(channelSubscribedRef.current);
          logger.info('[Notifications] Left channel:', channelSubscribedRef.current);
        }
        channelSubscribedRef.current = null;
        setEchoNotificationsActive(false);
      }
      if (echoInitializedRef.current) {
        disconnectEcho();
        echoInitializedRef.current = false;
        logger.info('[Notifications] Echo disconnected on logout');
      }
      return;
    }

    // #680 리뷰: 재방문 세션 푸시 토큰 갱신 — 이미 권한을 허용한 고객의 FCM 토큰을
    // 재발급/재등록하고 포그라운드 핸들러를 장착한다 (fire-and-forget, 실패는 내부 로깅).
    // Reverb 설정과 무관하게 실행되도록 reverbKey 가드보다 앞에 위치.
    if (customerId) {
      void refreshPushToken();
    }

    // Reverb 환경변수가 설정되어 있는 경우에만 Echo 초기화
    const reverbKey = import.meta.env.VITE_REVERB_APP_KEY;
    if (!reverbKey) {
      logger.info('[Notifications] Reverb not configured, skipping Echo initialization');
      return;
    }

    // customerId가 없으면 채널 구독 불가
    if (!customerId) {
      logger.info('[Notifications] customerId not available, skipping channel subscription');
      return;
    }

    // Echo 초기화
    if (!echoInitializedRef.current) {
      try {
        initializeEcho();
        echoInitializedRef.current = true;
        logger.info('[Notifications] Echo initialized');
      } catch (error) {
        logger.error('[Notifications] Failed to initialize Echo:', error);
        return;
      }
    }

    // 이미 같은 채널을 구독 중이면 스킵
    const channelName = `App.Models.Customer.${customerId}`;
    if (channelSubscribedRef.current === channelName) {
      return;
    }

    // 이전 채널 구독 해제
    if (channelSubscribedRef.current) {
      const echo = getEcho();
      if (echo) {
        echo.leave(channelSubscribedRef.current);
        logger.info('[Notifications] Left previous channel:', channelSubscribedRef.current);
      }
      setEchoNotificationsActive(false);
    }

    // Queued callbacks can run after leave(); bind them to this effect owner.
    const echoController = readScope.controller;
    const isCurrentChannel = () => isCurrentRead() && readScope.controller === echoController;

    // 새 채널 구독
    try {
      const echo = getEcho();
      if (!echo) {
        logger.error('[Notifications] Echo instance not available');
        return;
      }

      // subscribed/error: laravel-echo 2.2.6 PusherChannel 의 pusher:subscription_succeeded /
      // pusher:subscription_error 바인딩. 소켓이 끊겼다 재연결되면 pusher-js 가 모든 채널을
      // 재구독(subscribeAll → reinstateSubscription)하며 pusher:subscription_succeeded 를
      // 다시 발화하므로 subscribed 콜백이 재호출된다 — 별도 connection 리스너 불필요.
      echo.private(channelName)
        .subscribed(() => { if (isCurrentChannel()) setEchoNotificationsActive(true); })
        .error(() => { if (isCurrentChannel()) setEchoNotificationsActive(false); })
        .notification((notification: BroadcastNotificationPayload) => {
          if (!isCurrentChannel()) return;
          logger.info('[Notifications] Received real-time notification:', notification.notification_type);

          const transformed = transformBroadcastNotification(notification);

          // 중복 수신 (이미 목록에 존재) — 토스트/배지/목록 모두 스킵
          if (notificationsRef.current.some(n => n.id === transformed.id)) {
            logger.info('[Notifications] Duplicate notification ignored:', transformed.id);
            return;
          }
          // updateNotifications mirrors state synchronously for same-tick deduplication.
          toast(transformed.title, { id: transformed.id, description: transformed.message });

          updateNotifications(prev =>
            prev.some(n => n.id === transformed.id) ? prev : [transformed, ...prev]
          );

          if (!transformed.isRead) {
            setUnreadCount(prev => prev + 1);
          }
        });

      channelSubscribedRef.current = channelName;
      logger.info('[Notifications] Subscribed to channel:', channelName);
    } catch (error) {
      logger.error('[Notifications] Failed to subscribe to channel:', error);
    }

    // cleanup: 컴포넌트 언마운트 시
    return () => {
      if (channelSubscribedRef.current) {
        const echo = getEcho();
        if (echo) {
          echo.leave(channelSubscribedRef.current);
          logger.info('[Notifications] Left channel on cleanup:', channelSubscribedRef.current);
        }
        channelSubscribedRef.current = null;
        setEchoNotificationsActive(false);
      }
    };
  }, [isLoggedIn, customerId, isCurrentRead, readScope, updateNotifications]);

  // A write invalidates reads that started before it. Failed writes have an
  // ambiguous server outcome: reconcile with a GET instead of restoring a stale
  // snapshot or replaying the mutation. Wait until concurrent writes settle.
  const mutate = useCallback(async (write: () => Promise<unknown>, message: string) => {
    if (readScope.list) readScope.needsReconcile = true;
    readScope.revision += 1;
    readScope.pendingMutations += 1;
    readScope.list = null;
    readScope.unread = null;
    setIsLoading(false);
    try {
      await write();
    } catch (error) {
      if (isCurrentRead()) {
        logger.error(message, error);
        readScope.needsReconcile = true;
      }
    } finally {
      readScope.pendingMutations -= 1;
      if (isCurrentRead() && readScope.pendingMutations === 0 && readScope.needsReconcile) {
        readScope.needsReconcile = false;
        await fetchNotifications();
      }
    }
  }, [readScope, isCurrentRead, fetchNotifications]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!isCurrentRead()) return;
    const notification = notificationsRef.current.find(n => n.id === notificationId);
    if (!notification || notification.isRead) return;
    updateNotifications(previous => previous.map(n => n.id === notificationId ? { ...n, isRead: true } : n));
    setUnreadCount(previous => Math.max(0, previous - 1));
    if (isLoggedIn) await mutate(() => notificationApi.markAsRead(notificationId), '[Notifications] Failed to mark as read:');
  }, [isCurrentRead, isLoggedIn, mutate, updateNotifications]);

  const markAllAsRead = useCallback(async () => {
    if (!isCurrentRead()) return;
    updateNotifications(previous => previous.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
    if (isLoggedIn) await mutate(() => notificationApi.markAllAsRead(), '[Notifications] Failed to mark all as read:');
  }, [isCurrentRead, isLoggedIn, mutate, updateNotifications]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!isCurrentRead()) return;
    const deleted = notificationsRef.current.find(n => n.id === notificationId);
    if (!deleted) return;
    updateNotifications(previous => previous.filter(n => n.id !== notificationId));
    if (!deleted.isRead) setUnreadCount(previous => Math.max(0, previous - 1));
    if (isLoggedIn) await mutate(() => notificationApi.deleteNotification(notificationId), '[Notifications] Failed to delete:');
  }, [isCurrentRead, isLoggedIn, mutate, updateNotifications]);

  // 모든 알림 삭제 (로컬 전용)
  const deleteAllNotifications = useCallback(() => {
    updateNotifications([]);
    setUnreadCount(0);
  }, [updateNotifications]);

  // 새 알림 추가 (로컬)
  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `notification-${generateUUID()}`,
      timestamp: new Date(),
    };
    updateNotifications(prev => [newNotification, ...prev]);
    if (!notification.isRead) {
      setUnreadCount(prev => prev + 1);
    }
  }, [updateNotifications]);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      deleteAllNotifications,
      addNotification,
      refreshNotifications,
    }),
    [
      notifications,
      unreadCount,
      isLoading,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      deleteAllNotifications,
      addNotification,
      refreshNotifications,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

/**
 * useNotifications - NotificationsContext 사용 훅
 */
export function useNotifications(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}
